import { products, type Product } from "../data";

export type DemoOrderStatus = "new" | "fulfilled";

export type DemoOrder = {
  id: string;
  createdAt: string;
  items: { name: string; quantity: number; price: number }[];
  total: number;
  customer: { name: string; email: string };
  delivery: { address: string; city: string; postcode: string; preferredDate?: string };
  giftNote?: string;
  status: DemoOrderStatus;
};

export type DemoEnquiry = {
  id: string;
  createdAt: string;
  kind: "contact" | "corporate";
  name: string;
  email: string;
  company?: string;
  message: string;
  status: "open" | "closed";
};

export type NewsletterSignup = {
  id: string;
  createdAt: string;
  email: string;
};

export type BuilderSubmission = {
  id: string;
  createdAt: string;
  items: string[];
  colour: string;
  total: number;
};

export type ProductOverride = {
  description?: string;
  featured?: boolean;
};

const keys = {
  orders: "trio-demo-orders",
  enquiries: "trio-demo-enquiries",
  newsletter: "trio-demo-newsletter",
  builders: "trio-demo-builder-orders",
  products: "trio-demo-product-overrides"
} as const;

function read<T>(key: string, fallback: T): T {
  try {
    const value = localStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write<T>(key: string, value: T) {
  localStorage.setItem(key, JSON.stringify(value));
  window.dispatchEvent(new CustomEvent("trio-demo-data", { detail: key }));
}

function id(prefix: string) {
  const suffix = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID().slice(0, 8)
    : `${Date.now()}`.slice(-8);
  return `${prefix}-${suffix}`;
}

export function getOrders() {
  return read<DemoOrder[]>(keys.orders, []);
}

export function createOrder(order: Omit<DemoOrder, "id" | "createdAt" | "status">) {
  const next: DemoOrder = {
    ...order,
    id: id("TA"),
    createdAt: new Date().toISOString(),
    status: "new"
  };
  write(keys.orders, [next, ...getOrders()]);
  localStorage.setItem("trio-last-order", next.id);
  return next;
}

export function markOrderFulfilled(orderId: string, fulfilled: boolean) {
  const next = getOrders().map((order) =>
    order.id === orderId ? { ...order, status: fulfilled ? "fulfilled" : "new" } as DemoOrder : order
  );
  write(keys.orders, next);
  return next;
}

export function getEnquiries() {
  return read<DemoEnquiry[]>(keys.enquiries, []);
}

export function addEnquiry(enquiry: Omit<DemoEnquiry, "id" | "createdAt" | "status">) {
  const next: DemoEnquiry = {
    ...enquiry,
    id: id("ENQ"),
    createdAt: new Date().toISOString(),
    status: "open"
  };
  write(keys.enquiries, [next, ...getEnquiries()]);
  return next;
}

export function setEnquiryClosed(enquiryId: string, closed: boolean) {
  const next = getEnquiries().map((enquiry) =>
    enquiry.id === enquiryId ? { ...enquiry, status: closed ? "closed" : "open" } as DemoEnquiry : enquiry
  );
  write(keys.enquiries, next);
  return next;
}

export function getNewsletterSignups() {
  return read<NewsletterSignup[]>(keys.newsletter, []);
}

export function addNewsletterSignup(email: string) {
  const current = getNewsletterSignups();
  if (current.some((signup) => signup.email.toLowerCase() === email.toLowerCase())) return current;
  const next = [{ id: id("SUB"), createdAt: new Date().toISOString(), email }, ...current];
  write(keys.newsletter, next);
  return next;
}

export function getBuilderSubmissions() {
  return read<BuilderSubmission[]>(keys.builders, []);
}

export function addBuilderSubmission(submission: Omit<BuilderSubmission, "id" | "createdAt">) {
  const next: BuilderSubmission = {
    ...submission,
    id: id("BUILD"),
    createdAt: new Date().toISOString()
  };
  write(keys.builders, [next, ...getBuilderSubmissions()]);
  return next;
}

export function getProductOverrides() {
  return read<Record<string, ProductOverride>>(keys.products, {});
}

export function updateProductOverride(slug: string, override: ProductOverride) {
  const current = getProductOverrides();
  const next = { ...current, [slug]: { ...current[slug], ...override } };
  write(keys.products, next);
  return next;
}

export function getAdminProducts(): (Product & { featured: boolean })[] {
  const overrides = getProductOverrides();
  return products.map((product, index) => ({
    ...product,
    description: overrides[product.slug]?.description ?? product.description,
    featured: overrides[product.slug]?.featured ?? index < 3
  }));
}
